import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import { useAddUsersToRoleMutation, useOrganizationUsersFilterableQuery } from "../../../api/network/adminApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { LegacyUserEntityFilterableListingPayload, TLegacyUserWithAccount } from "../../../api/types/User";
import FTModal from "../../../components/common/FTModal";
import DualListboxPagedLHS from "../../../components/dualListboxPagedLHS";
import Pagination, { TPaginationSelected } from "../../../components/pagination";
import { TCustomizableItemList } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { useLoggedInUserData } from "../../../utils/user";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { ItemEnableButtonProps } from "../../../components/dualListboxCheckboxItem";
import svgIcons from "../../../assets/svg/svgIcons";

interface ModalAddUsersToRoleProps {
  roleId: string;
  existingUsers: string[];
}

const ModalAddUsersToRole: FC<ModalAddUsersToRoleProps> = ({ roleId, existingUsers }) => {
  const [selectedFields, setSelectedFields] = useState<TCustomizableItemList[]>([]);

  const [addUsersToRoleApiTrigger, { isLoading: isLoadingAddUsersToRole }] = useAddUsersToRoleMutation();

  const dispatch = useDispatch();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const { t } = useTranslation("translation", { keyPrefix: "admins.roles.detailsPage.add_new_users_modal" });
  const { t: tMain } = useTranslation("translation");

  // initial right list data
  const [initialRightListData, setInitialRightListData] = useState<TCustomizableItemList[]>([]);
  const [rightListItems, setRightListItems] = useState<TCustomizableItemList[]>([]); // initialRightListData

  // prepare query params for fetching organization users
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgUsersQueryParams, setOrgUsersQueryParams] = useState<LegacyUserEntityFilterableListingPayload>({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.LHS_RHS_PAGE_SIZE,
    search: "",
    not_include_roleid: roleId,
    exclude_ids: rightListItems.map((item) => item.id),
  });

  // fetching user data
  const {
    data: dataAddableOrgUsers,
    isFetching: isFetchingAddableOrgUsers,
    isUninitialized: isUninitializedAddableOrgUsers,
  } = useOrganizationUsersFilterableQuery(
    orgUsersQueryParams,
    {
      skip: !thisUserOrganizationId,
    }
  );
  const { /* count, next, previous, */ results } = dataAddableOrgUsers || {};

  // on change of dataAddableOrgUsers, scroll list to top
  useEffect(() => {
    if (leftContainerRef.current) {
      leftContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [dataAddableOrgUsers]);

  // on rightListItems change, update API params via setOrgUsersQueryParams
  useEffect(() => {
    if (isFetchingAddableOrgUsers || isUninitializedAddableOrgUsers) return;
    setOrgUsersQueryParams({
      ...orgUsersQueryParams,
      exclude_ids: rightListItems.map((item) => item.id),
    });
  }, [rightListItems, isFetchingAddableOrgUsers, isUninitializedAddableOrgUsers]);

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setOrgUsersQueryParams({ ...orgUsersQueryParams, search: value, page: 1 });
  }, 500);

  const leftContainerRef = React.useRef<HTMLDivElement>(null);
  const leftListData: TCustomizableItemList[] =
    !!results
      ? results
        .map((field) => ({
          id: field.userid.toString(),
          title: field.contactname || "-", // field.userid,
          sub_title: field.userid, // field.contactemail,
          metaData: field,
        })) : [];

  const hideModal = () => {
    setSelectedFields([]);
    dispatch(setModalsData({ ...modalsState, showAddUsersToRoleModal: false }));
  };

  // Filter out already selected fields
  const getLeftData = () => {
    let _leftListData = leftListData.filter((item) => selectedFields.findIndex((selected) => selected.id === item.id) === -1);
    _leftListData = _leftListData.filter((item) => existingUsers.indexOf(item.id) === -1);
    return _leftListData;
  }

  const handleUsersSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const _search = e.target.value;
    debouncedSetSearchKeyword(_search);
  }

  const handleSaveClick = (selectedItems: TCustomizableItemList[]) => {
    if (!selectedItems.length) {
      toast.error('Please select atleast one user');
      return;
    }

    const selectedUserIds = selectedItems.map(item => item.id).join(',');
    addUsersToRoleApiTrigger({
      roleid: roleId,
      user_ids: selectedUserIds,
    })
      .unwrap().then(() => {
        toast.success('User(s) added successfully');
        hideModal();
      })
      .catch((error) => {
        console.log('Error while adding user to the role', error);
        toast.error('Error while adding user(s) to the role');
      });
  }

  return (
    <FTModal
      visible={modalsState.showAddUsersToRoleModal}
      heading={t("modal_heading", {
        role: roleId
      })}
      onHide={hideModal}
      containerStyle="max-w-[calc(100vw-4rem)] w-[1016px]"
    >
      <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
        {t("sub_heading")}
      </p>
      <p className="mt-6 font-bold text-lg">
        {t("listing_heading")}
      </p>
      <div className="mt-5 col-span-6 grid grid-cols-12 gap-6">
        <DualListboxPagedLHS
          leftListContainerRef={leftContainerRef}
          leftLoading={isFetchingAddableOrgUsers || isUninitializedAddableOrgUsers}
          leftTitle={t("user_not_listed")}
          leftSearchKeyword={orgUsersQueryParams.search}
          leftSearchPlaceholder={t("users_search_placeholder")}
          leftSearchCallback={(e: React.ChangeEvent<HTMLInputElement>) => debouncedSetSearchKeyword(e.target.value)}
          leftList={leftListData}
          LeftPaginationComponent={<>
            {leftListData.length === 0 && (
              <>
                {/* <p className="text-[10px] text-gray-400 text-center mb-1 mx-2">{t("no_assets_found")}</p> */}
              </>
            )}
            {!!dataAddableOrgUsers?.count && (
              <Pagination
                pageSize={orgUsersQueryParams.page_size}
                totalPages={Math.ceil(dataAddableOrgUsers?.count / orgUsersQueryParams.page_size) || 0}
                forcePage={orgUsersQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  setOrgUsersQueryParams({ ...orgUsersQueryParams, page: data?.selected + 1 });
                }}
                onlyPageChange={true}
                size={1}
                wrapperClass="flex items-center justify-center"
              />
            )}
          </>}
          LeftItemEnableButton={({ title, disabled, handleEnable, metaData }) => {
            const userData = metaData as TLegacyUserWithAccount;
            if (!userData.roleid) return null;
            return (
              <div className="flex-grow shrink w-full flex items-center justify-start">
                <AdminFormFieldSubmit
                  // customWrapperClass="absolute right-0 top-1/2 -translate-y-1/2 mr-2"
                  customWrapperClass=""
                  buttonDimensionClass="h-auto min-h-6 px-1 py-2 bg-white mt-1"
                  buttonTextClass="text-sm font-display font-semibold break-words"
                  label={(
                    <>
                      <span className="text-[10px] leading-3 text-left ms-2 break-words">{userData.roleid}</span>
                      <img src={svgIcons.CrossRedIcon} alt="cross" className={`size-3 mx-2${disabled ? ' grayscale opacity-50' : ''}`} />
                    </>
                  )}
                  variant="danger"
                  disabled={disabled}
                  onClick={(e) => {
                    e?.preventDefault(); // prevents item to get selected on first click (over this button)
                    e?.stopPropagation();
                    handleEnable?.();
                  }}
                  titleText={t('move_user_from_role', { role: userData.roleid })}
                />
              </div>
            )
          }}
          disabled={isLoadingAddUsersToRole}
          rightTitle={t("users_selected")}
          rightSearchPlaceholder={t("default_search_placeholder")}
          // rightSearchCallback={handleRightSearchChange} // disabled to let the default (within DualListbox component) search callback run
          initialRightList={initialRightListData}
          rightListItems={rightListItems}
          setRightListItems={setRightListItems}
          handleCancelButton={hideModal}
          handleSaveButton={handleSaveClick}
          // groupPage={true}
          showInactiveEntities={true}
          showInactiveEntitiesText={false}
          saveButtonText={tMain("save")}
          showStatusTagOnItems={false}
          moveButtons={[2, 3, 4]}
        />
      </div>
    </FTModal>
  );
}

export default ModalAddUsersToRole;